import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './components/Layout/AppLayout';
import { AdminLayout } from './components/Layout/AdminLayout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { UsersPage } from './pages/UsersPage';
import { MatchesPage } from './pages/MatchesPage';
import { ClubsPage } from './pages/ClubsPage';
import { TournamentsPage } from './pages/TournamentsPage';
import { ScoresPage } from './pages/ScoresPage';
import { CalendarPage } from './pages/CalendarPage';
import { SettingsPage } from './pages/SettingsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import ChangelogPage from './pages/ChangelogPage';
import { TournamentPublicPage } from './pages/TournamentPublicPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminMatchesPage } from './pages/admin/AdminMatchesPage';
import { AdminTournamentsPage } from './pages/admin/AdminTournamentsPage';
import { AdminClubsPage } from './pages/admin/AdminClubsPage';
import { AdminCourtsPage } from './pages/admin/AdminCourtsPage';
import { AdminScoresPage } from './pages/admin/AdminScoresPage';
import { AdminAnalyticsPage } from './pages/admin/AdminAnalyticsPage';
import { AdminSystemPage } from './pages/admin/AdminSystemPage';
import { AdminNotificationsPage } from './pages/admin/AdminNotificationsPage';
import { AdminTimeSlotsPage } from './pages/admin/AdminTimeSlotsPage';
import { AdminTournamentManagementPage } from './pages/admin/AdminTournamentManagementPage';
import { AdminFeatureFlagsPage } from './pages/admin/AdminFeatureFlagsPage';
import { AdminEmailsPage } from './pages/admin/AdminEmailsPage';
import { AdminUserProgressPage } from './pages/admin/AdminUserProgressPage';
import { AdminUserDetailPage } from './pages/admin/AdminUserDetailPage';
import { ErrorBoundary } from './components/ErrorBoundary';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
      contrastText: '#fff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ed6c02',
    },
    info: {
      main: '#0288d1',
    },
    success: {
      main: '#2e7d32',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Login Page */}
      <Route path="/login" element={<LoginPage />} />

      {/* Reset Password - Public (from email link: /reset-password?token=xxx) */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Privacy Page - Public */}
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Changelog Page - Public */}
      <Route path="/changelog" element={<ChangelogPage />} />

      {/* Tournament Public Page - Shareable link, no auth */}
      <Route path="/t/:id" element={<TournamentPublicPage />} />
      
        {/* Admin Dashboard - Only accessible to admins */}
        <Route path="/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        
        {/* Protected User Routes */}
        <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
        <Route path="/matches" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
        <Route path="/clubs" element={<ProtectedRoute><ClubsPage /></ProtectedRoute>} />
        <Route path="/tournaments" element={<ProtectedRoute><TournamentsPage /></ProtectedRoute>} />
        <Route path="/scores" element={<ProtectedRoute><ScoresPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
        <Route path="/admin/users/:userId/progress" element={<AdminRoute><AdminUserProgressPage /></AdminRoute>} />
        <Route path="/admin/users/:userId" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
        <Route path="/admin/matches" element={<AdminRoute><AdminMatchesPage /></AdminRoute>} />
        <Route path="/admin/tournaments" element={<AdminRoute><AdminTournamentsPage /></AdminRoute>} />
        <Route path="/admin/clubs" element={<AdminRoute><AdminClubsPage /></AdminRoute>} />
        <Route path="/admin/courts" element={<AdminRoute><AdminCourtsPage /></AdminRoute>} />
        <Route path="/admin/scores" element={<AdminRoute><AdminScoresPage /></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
        <Route path="/admin/system" element={<AdminRoute><AdminSystemPage /></AdminRoute>} />
        <Route path="/admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
        <Route path="/admin/emails" element={<AdminRoute><AdminEmailsPage /></AdminRoute>} />
        <Route path="/admin/time-slots" element={<AdminRoute><AdminTimeSlotsPage /></AdminRoute>} />
        <Route path="/admin/calendar" element={<AdminRoute><CalendarPage /></AdminRoute>} />
        <Route path="/admin/tournament-management" element={<AdminRoute><AdminTournamentManagementPage /></AdminRoute>} />
        <Route path="/admin/feature-flags" element={<AdminRoute><AdminFeatureFlagsPage /></AdminRoute>} />
      
        {/* Catch-all route for unmatched paths */}
        <Route path="*" element={<Navigate to="/users" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
